import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

// Components
import { MessageDetailSearchModalComponent } from './message-detail-search-modal/message-detail-search-modal.component';
import { NavOverflowItem } from '@vg-constellation/angular-19/nav-overflow';

// Services
import { StatementMessagingHttpClientService } from '../../services/http-bridge/statement-messaging-http-client.service';
import { SelectedMessageDetailService } from '../../services/selected-message-detail/selected-message-detail.service';
import { MessageDataService } from '../../services/message-details/message-details.service';
import { AuthorizationService, UserPermissions } from '../../services/read-write-role/authorization.service';

// Models
import {
  StatementMessageModel,
  StatementMessagingMessageModel,
} from '../../core/interfaces/statement-messaging-run.model';

// Constants
import {
  ADD_MESSAGE_ACTION,
  CLEAR_ACTION,
  SEARCH_ACTION,
  SHOW_ACTION,
  VIEW_MESSAGE_ACTION,
} from '../../core/constants/statement-messaging.constant';

// Utilities
import { FilterUtil } from '../../core/utility/filter.util';

@Component({
  selector: 'app-message-detail-action',
  templateUrl: './message-detail-action.component.html',
  styleUrl: './message-detail-action.component.scss',
  standalone: false,
})
export class MessageDetailActionComponent implements OnInit, OnDestroy {
  // ============================================
  // View Children
  // ============================================
  @ViewChild(MessageDetailSearchModalComponent)
  searchMessageModalComponent!: MessageDetailSearchModalComponent;

  // ============================================
  // Public Properties
  // ============================================
  readonly messageDetailHeading = 'Message detail';
  hasMessageData = false;
  selectedMessageDetail: StatementMessageModel | null = null;

  permissions: UserPermissions = {
    canView: false,
    canEdit: false,
    canCreate: false,
    canDelete: false,
    isReadOnly: true,
  };

  // ============================================
  // Services
  // ============================================
  private readonly router = inject(Router);
  private readonly filterUtil = inject(FilterUtil);
  private readonly httpClient = inject(StatementMessagingHttpClientService);
  private readonly selectedMessageService = inject(SelectedMessageDetailService);
  private readonly messageDataService = inject(MessageDataService);
  protected readonly authService = inject(AuthorizationService);
  private readonly destroy$ = new Subject<void>();

  // ============================================
  // Lifecycle Hooks
  // ============================================

  ngOnInit(): void {
    this.initializeSubscriptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============================================
  // Initialization
  // ============================================

  /**
   * Initialize all component subscriptions
   */
  private initializeSubscriptions(): void {
    this.subscribeToAuthorization();
    this.subscribeToMessageSelection();
    this.subscribeToMessageDataState();
  }

  // ============================================
  // Subscriptions
  // ============================================

  /**
   * Subscribe to authorization changes
   */
  private subscribeToAuthorization(): void {
    this.authService.permissions$
      .pipe(takeUntil(this.destroy$))
      .subscribe((permissions) => {
        this.permissions = permissions;
      });
  }

  /**
   * Subscribe to message selection changes
   */
  private subscribeToMessageSelection(): void {
    this.selectedMessageService.selectedMessage$
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedMessage) => {
        this.selectedMessageDetail = selectedMessage;
      });
  }

  /**
   * Subscribe to message data state changes
   */
  private subscribeToMessageDataState(): void {
    this.messageDataService.messageData$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.hasMessageData = state.data.statementMessages.length > 0;
      });
  }

  // ============================================
  // Menu Actions
  // ============================================

  /**
   * Handle menu item clicks
   */
  onMenuItemClick(item: NavOverflowItem): void {
    const actionMap = this.getActionMap();
    const action = actionMap.get(item.eventId || '');

    if (action) {
      action();
    }
  }

  /**
   * Get action map for menu items
   */
  private getActionMap(): Map<string, () => void> {
    return new Map([
      [SEARCH_ACTION.eventId, () => this.onMessageSearch()],
      [SHOW_ACTION.eventId, () => this.onMessageShowAll()],
      [CLEAR_ACTION.eventId, () => this.onMessageClear()],
      [ADD_MESSAGE_ACTION.eventId, () => this.onAddMessage()],
      [VIEW_MESSAGE_ACTION.eventId, () => this.onViewMessage()],
    ]);
  }

  // ============================================
  // Search & Filter Actions
  // ============================================

  /**
   * Open search modal
   */
  onMessageSearch(): void {
    this.searchMessageModalComponent.openSearchModal();
  }

  /**
   * Show all messages with optional filters
   */
  onMessageShowAll(searchCriteria?: Record<string, string>): void {
    this.messageDataService.setLoading(true);
    this.fetchMessageDetails(searchCriteria);
  }

  /**
   * Handle search form submission
   */
  handleMessageFormSubmit(formData: Record<string, string>): void {
    this.onMessageShowAll(formData);
  }

  /**
   * Clear all data and selections
   */
  onMessageClear(): void {
    this.messageDataService.clearData();
    this.selectedMessageService.clearSelection();
  }

  // ============================================
  // Data Fetching
  // ============================================

  /**
   * Fetch message details from API
   */
  private fetchMessageDetails(searchCriteria?: Record<string, string>): void {
    this.httpClient
      .getMessageDetails()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => this.handleMessageDataSuccess(data, searchCriteria),
        error: (error) => this.handleMessageDataError(error),
      });
  }

  /**
   * Handle successful message data response
   */
  private handleMessageDataSuccess(
    data: StatementMessagingMessageModel,
    searchCriteria?: Record<string, string>,
  ): void {
    if (this.hasCriteria(searchCriteria)) {
      this.applySearchFilters(data, searchCriteria!);
    } else {
      this.setMessageData(data);
    }
  }

  /**
   * Check if search criteria exists and is not empty
   */
  private hasCriteria(criteria?: Record<string, string>): boolean {
    return !!criteria && Object.keys(criteria).length > 0;
  }

  /**
   * Set message data and clear selection
   */
  private setMessageData(data: StatementMessagingMessageModel): void {
    this.messageDataService.setMessageData(data);
    this.selectedMessageService.clearSelection();
  }

  /**
   * Handle message data error
   */
  private handleMessageDataError(error: Error): void {
    this.messageDataService.setError(error.message);
    this.selectedMessageService.clearSelection();
  }

  // ============================================
  // Search Filtering
  // ============================================

  /**
   * Apply search filters to message data
   */
  private applySearchFilters(
    data: StatementMessagingMessageModel,
    criteria: Record<string, string>,
  ): void {
    const filteredData = this.filterMessageData(data, criteria);

    if (this.hasNoResults(filteredData)) {
      this.setNoResultsState();
    } else {
      this.setMessageData(filteredData);
    }
  }

  /**
   * Filter message data by criteria
   */
  private filterMessageData(
    data: StatementMessagingMessageModel,
    criteria: Record<string, string>,
  ): StatementMessagingMessageModel {
    return {
      statementMessages: this.filterUtil.filterMessageDataAdvanced(
        data.statementMessages,
        criteria,
      ),
    };
  }

  /**
   * Check if filtered data has no results
   */
  private hasNoResults(data: StatementMessagingMessageModel): boolean {
    return data.statementMessages.length === 0;
  }

  /**
   * Set no results state
   */
  private setNoResultsState(): void {
    this.messageDataService.updateState({
      data: { statementMessages: [] },
      errorMessage: 'No data found matching criteria',
      isLoading: false,
    });
    this.selectedMessageService.clearSelection();
  }

  // ============================================
  // Navigation Actions
  // ============================================

  /**
   * Navigate to add message page
   */
  onAddMessage(): void {
    this.navigateTo('/message/add');
  }

  /**
   * Navigate to view message page
   */
  onViewMessage(): void {
    if (!this.selectedMessageDetail) {
      return;
    }
    this.navigateTo('/message/view');
  }

  /**
   * Navigate to specified route
   */
  private navigateTo(route: string): void {
    this.router.navigate([route]).then(
      () => {
        // Navigation successful
      },
      (error: Error) => {
        // Navigation failed - error is logged by router
        console.error('Navigation error:', error);
      },
    );
  }
}
